/**
 * Admin Help Sections Page
 *
 * CRUD for the context-aware Help assistant topics (skillsnap-help table).
 * Each topic has steps; a step can point its "Show me" button at a real UI
 * element via a highlight target chosen from HIGHLIGHT_TARGETS.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import {
  Save, Loader2, AlertCircle, CheckCircle, Plus, Trash2,
  ChevronDown, ChevronRight, X, RefreshCw, ArrowUp, ArrowDown,
  ToggleLeft, ToggleRight, Database, Lightbulb,
} from 'lucide-react';
import { HELP_TARGETS } from '../../help/highlightTargets';
import { DEFAULT_CONTEXT_PRIORITY, normalizeContextPriority } from '../../components/HelpPanel';
import type { HelpTopic, HelpStep, HelpContextKey, HelpTopicType, HelpStepType } from '../../help/types';

const CONTEXT_KEYS: { value: HelpContextKey; label: string }[] = [
  { value: '', label: 'None (browse only)' },
  { value: 'no_resume', label: 'Default when user has 0 resumes' },
  { value: 'no_jobs', label: 'Default when user has 0 jobs' },
  { value: 'no_fingerprint', label: 'Default when user has no Writing Fingerprint' },
  { value: 'pipeline', label: 'Default when user has 1–50 jobs' },
  { value: 'tips_pool', label: 'Default when user has >50 jobs' },
];

const TOPIC_TYPES: { value: HelpTopicType; label: string }[] = [
  { value: 'topic', label: 'Topic (numbered steps)' },
  { value: 'super', label: 'Super (bundles child topics)' },
  { value: 'tip', label: 'Tip pool (one shown at random)' },
];

function blankTopic(): HelpTopic {
  return {
    topic_id: '',
    title: '',
    short_desc: '',
    category: 'General',
    context_key: '',
    topic_type: 'topic',
    children: [],
    steps: [{ text: '' }],
    sort_order: 100,
    enabled: true,
  };
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500';

export function HelpSections() {
  const [topics, setTopics] = useState<HelpTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<HelpTopic | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await api.adminListHelpTopics();
      setTopics(list);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load help topics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flashSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  }

  function startEdit(topic: HelpTopic) {
    setExpandedId(topic.topic_id);
    setIsNew(false);
    setDraft(JSON.parse(JSON.stringify(topic)));
  }

  function startNew() {
    setExpandedId('__new__');
    setIsNew(true);
    setDraft(blankTopic());
  }

  function cancelEdit() {
    setExpandedId(null);
    setDraft(null);
    setIsNew(false);
  }

  function patch(p: Partial<HelpTopic>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
  }

  // --- step helpers ---
  function patchStep(i: number, p: Partial<HelpStep>) {
    setDraft((d) => {
      if (!d) return d;
      const steps = d.steps.map((s, idx) => (idx === i ? { ...s, ...p } : s));
      return { ...d, steps };
    });
  }
  function addStep(stepType?: HelpStepType) {
    setDraft((d) => (d
      ? { ...d, steps: [...d.steps, stepType === 'tip' ? { text: '', step_type: 'tip' as const } : { text: '' }] }
      : d));
  }
  function removeStep(i: number) {
    setDraft((d) => (d ? { ...d, steps: d.steps.filter((_, idx) => idx !== i) } : d));
  }
  function moveStep(i: number, dir: -1 | 1) {
    setDraft((d) => {
      if (!d) return d;
      const j = i + dir;
      if (j < 0 || j >= d.steps.length) return d;
      const steps = [...d.steps];
      [steps[i], steps[j]] = [steps[j], steps[i]];
      return { ...d, steps };
    });
  }

  function toggleChild(id: string) {
    setDraft((d) => {
      if (!d) return d;
      const has = d.children.includes(id);
      return { ...d, children: has ? d.children.filter((c) => c !== id) : [...d.children, id] };
    });
  }

  async function handleSave() {
    if (!draft) return;
    if (!draft.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const idForPath = isNew ? (draft.topic_id.trim() || draft.title) : draft.topic_id;
      const { topic } = await api.adminSaveHelpTopic(idForPath, draft);
      flashSuccess(`Saved "${topic.title}"`);
      await load();
      setExpandedId(topic.topic_id);
      setIsNew(false);
      setDraft(topic);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save topic');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!draft || isNew) return;
    if (!confirm(`Delete help topic "${draft.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await api.adminDeleteHelpTopic(draft.topic_id);
      flashSuccess(`Deleted "${draft.title}"`);
      cancelEdit();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete topic');
    } finally {
      setDeleting(false);
    }
  }

  async function handleSeed() {
    if (!confirm('Seed default help topics? Existing topics are preserved (only missing ones are added).')) return;
    setSeeding(true);
    setError(null);
    try {
      const res = await api.adminSeedHelpTopics(false);
      flashSuccess(res.message || `Seeded ${res.created}, skipped ${res.skipped}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to seed topics');
    } finally {
      setSeeding(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 flex gap-6 items-start">
      <div className="max-w-4xl flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Help Docs</h1>
            <p className="text-sm text-gray-500 mt-1">
              {topics.length} topics in <code className="bg-gray-100 px-1 rounded text-xs">skillsnap-help</code>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Seed Defaults
            </button>
            <button
              onClick={startNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4" />
              New Topic
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* New topic editor */}
        {isNew && draft && expandedId === '__new__' && (
          <div className="mb-4 bg-white rounded-lg border border-primary-300 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-900">New Topic</div>
            <div className="p-4">
              <TopicEditor
                draft={draft}
                isNew
                allTopics={topics}
                patch={patch}
                patchStep={patchStep}
                addStep={addStep}
                removeStep={removeStep}
                moveStep={moveStep}
                toggleChild={toggleChild}
                onSave={handleSave}
                onCancel={cancelEdit}
                onDelete={handleDelete}
                saving={saving}
                deleting={deleting}
              />
            </div>
          </div>
        )}

        {/* Topic list */}
        <div className="space-y-3">
          {topics.map((topic) => {
            const expanded = expandedId === topic.topic_id;
            return (
              <div key={topic.topic_id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 rounded-t-lg"
                  onClick={() => (expanded ? cancelEdit() : startEdit(topic))}
                >
                  {expanded
                    ? <ChevronDown className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />}
                  {topic.topic_type === 'tip' && <Lightbulb className="w-4 h-4 text-amber-500 mr-1.5 flex-shrink-0" />}
                  <span className="font-semibold text-sm text-gray-900 mr-2">{topic.title}</span>
                  <code className="text-xs text-gray-400 mr-2">{topic.topic_id}</code>
                  {!topic.enabled && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full mr-2">disabled</span>
                  )}
                  {topic.context_key && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mr-2">{topic.context_key}</span>
                  )}
                  <span className="ml-auto text-xs text-gray-400">{topic.category}</span>
                </div>

                {expanded && draft && !isNew && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="pt-4">
                      <TopicEditor
                        draft={draft}
                        isNew={false}
                        allTopics={topics}
                        patch={patch}
                        patchStep={patchStep}
                        addStep={addStep}
                        removeStep={removeStep}
                        moveStep={moveStep}
                        toggleChild={toggleChild}
                        onSave={handleSave}
                        onCancel={cancelEdit}
                        onDelete={handleDelete}
                        saving={saving}
                        deleting={deleting}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {topics.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No help topics yet. Click <b>Seed Defaults</b> to load the starter set, or <b>New Topic</b>.
          </div>
        )}
      </div>

      {/* Authoring guide + target catalog (right pane) */}
      <AuthoringGuide topics={topics} />
      </div>
    </div>
  );
}

/**
 * Right-hand authoring reference: how the help system picks a default topic
 * (it's DYNAMIC — driven by each topic's Context field, no code change
 * needed), what every field means, and a searchable catalog of all
 * `data-help-target` keys steps can point their "Show me" buttons at.
 */
/** Collapsible card for the authoring pane — click the header to toggle. */
function GuideSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm text-sm text-gray-700">
      {/* Password managers get confused by accordion buttons — suppress (LEARNINGS). */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-lpignore="true"
        data-form-type="other"
        data-1p-ignore="true"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
        <span className="font-semibold text-gray-900">{title}</span>
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function AuthoringGuide({ topics }: { topics: HelpTopic[] }) {
  // Accordion state — collapsed by default so the pane stays compact.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    defaults: true, // most-asked question stays visible on load
    fields: false,
    targets: false,
  });
  const toggleSection = (k: string) =>
    setOpenSections((prev) => ({ ...prev, [k]: !prev[k] }));
  const [targetSearch, setTargetSearch] = useState('');
  // Live bucket priority from the help_context_priority global setting
  // (falls back to the code default when unset/unreadable).
  const [contextPriority, setContextPriority] = useState<string[]>(DEFAULT_CONTEXT_PRIORITY);
  useEffect(() => {
    let cancelled = false;
    api.getPublicSetting('help_context_priority')
      .then((res) => { if (!cancelled) setContextPriority(normalizeContextPriority(res.setting_value)); })
      .catch(() => { /* keep default */ });
    return () => { cancelled = true; };
  }, []);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const filteredTargets = useMemo(() => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return HELP_TARGETS;
    return HELP_TARGETS.filter(
      (t) => t.key.toLowerCase().includes(q) || t.label.toLowerCase().includes(q),
    );
  }, [targetSearch]);

  // Which context buckets are currently claimed (and by which topic)?
  const contextOwners = useMemo(() => {
    const owners = new Map<string, string>();
    [...topics]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((t) => {
        if (t.context_key && t.enabled && !owners.has(t.context_key)) {
          owners.set(t.context_key, t.topic_id);
        }
      });
    return owners;
  }, [topics]);

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1200);
  };

  return (
    <aside className="hidden lg:block w-96 shrink-0 space-y-3 sticky top-6">
      {/* How defaults work */}
      <GuideSection
        title="How the default topic is chosen"
        open={!!openSections.defaults}
        onToggle={() => toggleSection('defaults')}
      >
        <p className="text-xs leading-relaxed mb-2">
          Defaulting is <b>dynamic — no code change needed</b>. When a user opens Help,
          each bucket below is checked <b>in priority order</b>; the first whose condition
          is true AND that an enabled topic claims wins. The order comes from the{' '}
          <code>help_context_priority</code> setting in <b>Global Settings</b> — edit it
          there (a JSON list of bucket names) to re-prioritize without a deploy:
        </p>
        <ul className="text-xs space-y-1 mb-2">
          {contextPriority.map((ck, i) => {
            const meta = CONTEXT_KEYS.find((c) => c.value === ck);
            return (
              <li key={ck} className="flex items-center gap-2">
                <span className="text-gray-400 w-3 text-right">{i + 1}.</span>
                <code className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{ck}</code>
                <span className="text-gray-500">{(meta?.label || ck).replace('Default when user has ', '')}</span>
                {contextOwners.has(ck) ? (
                  <code className="ml-auto text-green-700 bg-green-50 px-1.5 py-0.5 rounded truncate max-w-28" title={contextOwners.get(ck)}>
                    {contextOwners.get(ck)}
                  </code>
                ) : (
                  <span className="ml-auto text-amber-600 text-[11px]">unclaimed!</span>
                )}
              </li>
            );
          })}
        </ul>
        <p className="text-xs leading-relaxed text-gray-500">
          So &ldquo;if the user has no jobs, default to Y&rdquo; = set Y&apos;s Context to{' '}
          <code>no_jobs</code>. Keep exactly ONE enabled topic per context — if two claim
          it, the lower sort order wins. A true-but-unclaimed bucket falls through to the
          next true one; if nothing matches, the very first topic in the list is shown.
        </p>
      </GuideSection>

      {/* Field guide */}
      <GuideSection
        title="Filling out a topic"
        open={!!openSections.fields}
        onToggle={() => toggleSection('fields')}
      >
        <dl className="text-xs space-y-1.5">
          <div><dt className="inline font-semibold">Title / Short description</dt><dd className="inline text-gray-500"> — what users see in the Help index. The description should name the problem the topic solves.</dd></div>
          <div><dt className="inline font-semibold">Topic ID</dt><dd className="inline text-gray-500"> — slug, auto-derived from the title on create; fixed afterwards.</dd></div>
          <div><dt className="inline font-semibold">Category</dt><dd className="inline text-gray-500"> — free-text label that groups topics in the index.</dd></div>
          <div><dt className="inline font-semibold">Type</dt><dd className="inline text-gray-500"> — <b>Topic</b>: numbered how-to steps. <b>Super</b>: bundles child topics inline (pick them under Child topics). <b>Tip pool</b>: each step is a standalone tip; one shows at random with an &ldquo;Another tip&rdquo; button.</dd></div>
          <div><dt className="inline font-semibold">Sort order</dt><dd className="inline text-gray-500"> — position in the index; also breaks ties for context defaults.</dd></div>
          <div><dt className="inline font-semibold">Enabled</dt><dd className="inline text-gray-500"> — off hides the topic from users (admins still see it here).</dd></div>
          <div><dt className="inline font-semibold">Step text</dt><dd className="inline text-gray-500"> — supports simple inline HTML (<code>&lt;b&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;a target=&quot;_blank&quot;&gt;</code>). Put <code>{'{{target}}'}</code> where the &ldquo;Show me&rdquo; button should appear.</dd></div>
          <div><dt className="inline font-semibold">Highlight target</dt><dd className="inline text-gray-500"> — the UI element the &ldquo;Show me&rdquo; button scrolls to and flashes (pick from the catalog below). <b>Button label</b> renames the button; <b>Disabled hover text</b> explains why it&apos;s inactive on the wrong page; <b>Open first</b> lists targets to click first to reveal nested controls.</dd></div>
          <div><dt className="inline font-semibold">Step type &ldquo;tip&rdquo;</dt><dd className="inline text-gray-500"> — renders unnumbered with a lightbulb, between the numbered steps.</dd></div>
        </dl>
      </GuideSection>

      {/* Target catalog */}
      <GuideSection
        title="Highlight-target catalog"
        open={!!openSections.targets}
        onToggle={() => toggleSection('targets')}
      >
        <p className="text-xs text-gray-500 mb-2">
          Every <code>data-help-target</code> a step can point at ({HELP_TARGETS.length} total).
          Click a key to copy it. Prefixes: <code>mainapp.*</code> dashboard,{' '}
          <code>answers.*</code> Application Q&amp;A, <code>interview.*</code> Interview Q&amp;A,{' '}
          <code>writingsample.*</code> Writing Sample lightbox.
        </p>
        <input
          type="text"
          value={targetSearch}
          onChange={(e) => setTargetSearch(e.target.value)}
          placeholder="Filter by key or label…"
          className={inputCls + ' mb-2'}
        />
        <div className="overflow-y-auto max-h-96 border border-gray-100 rounded">
          {filteredTargets.slice(0, 300).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => copyKey(t.key)}
              className="w-full text-left px-2 py-1.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              title="Click to copy the key"
            >
              <code className={`text-[11px] ${copiedKey === t.key ? 'text-green-700' : 'text-primary-700'}`}>
                {copiedKey === t.key ? 'copied!' : t.key}
              </code>
              <span className="block text-[11px] text-gray-500 truncate">{t.label}</span>
            </button>
          ))}
          {filteredTargets.length === 0 && (
            <div className="px-2 py-3 text-xs text-gray-400">No targets match.</div>
          )}
        </div>
      </GuideSection>
    </aside>
  );
}

// Searchable picker over the (large) hierarchical target catalog: type to filter
// by key or label, then choose. Shows the dotted key + human label per option.
function TargetPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return HELP_TARGETS;
    return HELP_TARGETS.filter((t) => t.key.toLowerCase().includes(s) || t.label.toLowerCase().includes(s));
  }, [q]);
  return (
    <div className="space-y-1">
      <input
        className={`${inputCls} bg-white`}
        placeholder="Filter targets… (e.g. addjob, resume, ready)"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <select
        className={`${inputCls} bg-white font-mono text-xs`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— none —</option>
        {value && !filtered.some((t) => t.key === value) && <option value={value}>{value}</option>}
        {filtered.slice(0, 300).map((t) => (
          <option key={t.key} value={t.key}>{t.key} — {t.label}</option>
        ))}
      </select>
      {q && <p className="text-[11px] text-gray-400">{filtered.length} match{filtered.length === 1 ? '' : 'es'}</p>}
    </div>
  );
}

interface EditorProps {
  draft: HelpTopic;
  isNew: boolean;
  allTopics: HelpTopic[];
  patch: (p: Partial<HelpTopic>) => void;
  patchStep: (i: number, p: Partial<HelpStep>) => void;
  addStep: (stepType?: HelpStepType) => void;
  removeStep: (i: number) => void;
  moveStep: (i: number, dir: -1 | 1) => void;
  toggleChild: (id: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  saving: boolean;
  deleting: boolean;
}

function TopicEditor(props: EditorProps) {
  const { draft, isNew, allTopics, patch, patchStep, addStep, removeStep, moveStep, toggleChild } = props;
  const showSteps = draft.topic_type !== 'super';
  const showChildren = draft.topic_type === 'super';
  // Tips don't consume a step number — mirror the user-facing StepList numbering.
  let stepCounter = 0;
  const stepNumbers = draft.steps.map((s) =>
    draft.topic_type === 'topic' && s.step_type === 'tip' ? null : ++stepCounter,
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input className={inputCls} value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Topic ID {isNew ? '(optional — derived from title)' : '(read-only)'}
          </label>
          <input
            className={`${inputCls} font-mono ${isNew ? '' : 'bg-gray-100 text-gray-500'}`}
            value={draft.topic_id}
            readOnly={!isNew}
            onChange={(e) => patch({ topic_id: e.target.value })}
            placeholder="auto"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <input className={inputCls} value={draft.category} onChange={(e) => patch({ category: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sort order</label>
          <input
            type="number"
            className={inputCls}
            value={draft.sort_order}
            onChange={(e) => patch({ sort_order: parseInt(e.target.value || '0', 10) })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <select className={inputCls} value={draft.topic_type} onChange={(e) => patch({ topic_type: e.target.value as HelpTopicType })}>
            {TOPIC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Context (default trigger)</label>
          <select className={inputCls} value={draft.context_key} onChange={(e) => patch({ context_key: e.target.value as HelpContextKey })}>
            {CONTEXT_KEYS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Short description (the problem this solves)
        </label>
        <textarea
          className={`${inputCls} resize-y`}
          rows={2}
          value={draft.short_desc}
          onChange={(e) => patch({ short_desc: e.target.value })}
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <button type="button" onClick={() => patch({ enabled: !draft.enabled })}>
          {draft.enabled ? <ToggleRight className="w-9 h-9 text-green-600" /> : <ToggleLeft className="w-9 h-9 text-gray-400" />}
        </button>
        <span className="text-sm text-gray-700">{draft.enabled ? 'Enabled (visible to users)' : 'Disabled (hidden)'}</span>
      </label>

      {/* Children (super topics) */}
      {showChildren && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Child topics (shown in order checked)
          </label>
          <div className="border border-gray-200 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
            {allTopics.filter((t) => t.topic_id !== draft.topic_id).map((t) => {
              const idx = draft.children.indexOf(t.topic_id);
              const checked = idx >= 0;
              return (
                <label key={t.topic_id} className="flex items-center gap-2 text-sm text-gray-700 px-1 py-0.5">
                  <input type="checkbox" checked={checked} onChange={() => toggleChild(t.topic_id)} />
                  {checked && <span className="text-xs text-primary-600 font-semibold w-4">{idx + 1}</span>}
                  <span>{t.title}</span>
                  <code className="text-xs text-gray-400">{t.topic_id}</code>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Steps */}
      {showSteps && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-gray-600">
              {draft.topic_type === 'tip' ? 'Tips (one shown at random)' : 'Steps'}
            </label>
            <div className="flex items-center gap-3">
              <button onClick={() => addStep()} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Add {draft.topic_type === 'tip' ? 'tip' : 'step'}
              </button>
              {draft.topic_type === 'topic' && (
                <button onClick={() => addStep('tip')} className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1">
                  <Lightbulb className="w-3.5 h-3.5" /> Add tip
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Text supports inline HTML: <code>&lt;b&gt;</code>, <code>&lt;code&gt;</code>, and links
            (<code>&lt;a href="…" target="_blank"&gt;</code>).
          </p>
          <div className="space-y-3">
            {draft.steps.map((step, i) => {
              const isTip = draft.topic_type === 'topic' && step.step_type === 'tip';
              return (
              <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start gap-2">
                  {isTip ? (
                    <span className="mt-2 flex w-5 justify-end" title="Tip (unnumbered)">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                    </span>
                  ) : (
                    <span className="mt-2 text-xs font-semibold text-gray-400 w-5 text-right">{stepNumbers[i]}</span>
                  )}
                  <div className="flex-1 space-y-2">
                    <textarea
                      className={`${inputCls} resize-y bg-white`}
                      rows={2}
                      value={step.text}
                      onChange={(e) => patchStep(i, { text: e.target.value })}
                      placeholder={isTip ? 'Tip text…' : 'Step text…'}
                    />
                    {draft.topic_type === 'topic' && (
                      <div className="flex items-center gap-2">
                        <label className="text-[11px] text-gray-500">Type</label>
                        <select
                          className="px-2 py-1 border border-gray-300 rounded-md text-xs bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                          value={isTip ? 'tip' : 'step'}
                          onChange={(e) => patchStep(i, { step_type: e.target.value === 'tip' ? 'tip' : undefined })}
                        >
                          <option value="step">Step (numbered)</option>
                          <option value="tip">Tip (lightbulb, unnumbered)</option>
                        </select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-0.5">Highlight target (optional)</label>
                        <TargetPicker
                          value={step.highlight_target || ''}
                          onChange={(v) => patchStep(i, { highlight_target: v || undefined })}
                        />
                      </div>
                      {step.highlight_target && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-0.5">Button label</label>
                            <input
                              className={`${inputCls} bg-white`}
                              value={step.button_label || ''}
                              onChange={(e) => patchStep(i, { button_label: e.target.value })}
                              placeholder="Show me"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-gray-500 mb-0.5">Disabled hover text (optional)</label>
                            <input
                              className={`${inputCls} bg-white`}
                              value={step.disabled_hint || ''}
                              onChange={(e) => patchStep(i, { disabled_hint: e.target.value || undefined })}
                              placeholder="overrides default e.g. “Add a job to see this”"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-[11px] text-gray-500 mb-0.5">
                              Open first (optional) — comma-separated target keys clicked before highlighting
                            </label>
                            <input
                              className={`${inputCls} bg-white font-mono text-xs`}
                              value={(step.open_first || []).join(', ')}
                              onChange={(e) => patchStep(i, { open_first: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                              placeholder="mainapp.addjob, mainapp.addjob.fromurl"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => moveStep(i, 1)} disabled={i === draft.steps.length - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => removeStep(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <button
            onClick={props.onSave}
            disabled={props.saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
          >
            {props.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {props.saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={props.onCancel} className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
        </div>
        {!isNew && (
          <button
            onClick={props.onDelete}
            disabled={props.deleting}
            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1.5 text-sm"
          >
            {props.deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
